import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { BarController, BarElement, CategoryScale, Chart, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import { AffiliateAnalytics } from '../../../../../models/interfaces/affiliate_analytics.interface';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { ModalService } from '../../../../../core/services/modal/modal.service';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { DateTime } from 'luxon';
import { cloneDeep } from 'lodash-es';
import { CustomValidator } from '../../../../../shared/validators/custom_validator';
import { firstValueFrom } from 'rxjs';
import { AffiliatePayment } from '../../../../../models/interfaces/affiliate_payment.interface';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';

Chart.register(CategoryScale, LinearScale, BarController, BarElement, Title, Legend, Tooltip);

@Component({
    selector: 'app-modal-affiliate-sales',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        MatTabsModule,
        MatDialogModule,
        MatProgressSpinnerModule
    ],
    templateUrl: './modal-affiliate-sales.component.html',
    styleUrl: './modal-affiliate-sales.component.scss'
})
export class ModalAffiliateSalesComponent implements OnInit {
  isLoadingAffiliate = true;
  fullname!: string;
  affiliateAnalytics!: AffiliateAnalytics;
  screenWidth = 0;
  monthSalesBarChart!: Chart;
  currentSalesText!: string;
  filterSalesForm: FormGroup = new FormGroup({});
  isFiltering = false;
  affiliateId!: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalAffiliateSalesComponent>,
    private api: ApiService,
    private toast: ToastService,
    private modal: ModalService,
    private alert: AlertService
  ) {}

  ngOnInit(): void {
    this.currentSalesText = DateTime.now().setLocale('es-ES').monthLong;
    this.initForm();
  }

  async initForm(): Promise<void> {
    try {
      const receivedData = this.data ? cloneDeep(this.data) : null;
      const { affiliateId, fullname } = receivedData;

      if (!affiliateId || !fullname) return this.closeModal();

      this.affiliateId = affiliateId;
      this.filterSalesForm = new FormGroup(
        {
          startDate: new FormControl(null, Validators.required),
          endDate: new FormControl(null, Validators.required),
          granularity: new FormControl('day', Validators.required)
        },
        { validators: CustomValidator.validateDateBefore }
      );

      this.fullname = fullname;
      const params = { affiliateId: this.affiliateId };
      const response = await firstValueFrom(this.api.fetchAffiliateSales(params));
      this.screenWidth = window.innerWidth;
      this.isLoadingAffiliate = false;
      this.affiliateAnalytics = response.analytics;
      setTimeout(() => {
        this.createChart(this.affiliateAnalytics.groupedOrders);
      }, 100);
    } catch (errorResponse: any) {
      this.isLoadingAffiliate = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error buscando estadísticas del afiliado');
    }
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  @HostListener('window:resize', ['$event'])
  getScreenSize(event = null) {
    this.screenWidth = window.innerWidth;
  }

  createChart(orders: Array<any>) {
    if (!orders.length) return;

    const xLabels = [];
    const yValues = [];
    for (const order of orders) {
      xLabels.push(order.date);
      yValues.push(order.total);
    }
    if (this.monthSalesBarChart) {
      this.monthSalesBarChart.destroy();
    }

    this.monthSalesBarChart = new Chart('monthSalesBarChart', {
      type: 'bar',
      data: {
        labels: xLabels,
        datasets: [
          {
            label: 'Ventas netas',
            data: yValues,
            backgroundColor: '#f1ae2e',
            borderRadius: 10,
            barThickness: 50
          }
        ]
      },
      options: {
        responsive: true,
        scales: {
          x: {
            display: true
          },
          y: {
            display: true
          }
        },
        aspectRatio: 2
      }
    });
  }

  async filterSales(): Promise<void> {
    try {
      this.isFiltering = true;
      const formValue = this.filterSalesForm.value;
      const startDate = DateTime.fromFormat(formValue.startDate, 'yyyy-LL-dd');
      const endDate = DateTime.fromFormat(formValue.endDate, 'yyyy-LL-dd');
      const params = {
        startDate: startDate.startOf('day').toUTC().toISO(),
        endDate: endDate.endOf('day').toUTC().toISO(),
        granularity: formValue.granularity,
        affiliateId: this.affiliateId
      };
      const response = await firstValueFrom(this.api.filterAffiliateSales(params));
      this.isFiltering = false;
      this.affiliateAnalytics.orders = response.analytics.orders;
      this.affiliateAnalytics.groupedOrders = response.analytics.groupedOrders;
      this.affiliateAnalytics.totalFee = response.analytics.totalFee;
      this.currentSalesText = `${startDate.setLocale('es-ES').toFormat('LLLL dd')} a ${endDate.setLocale('es-ES').toFormat('LLLL dd')}`;
      setTimeout(() => {
        this.createChart(this.affiliateAnalytics.groupedOrders);
      }, 100);
    } catch (errorResponse: any) {
      this.isFiltering = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error filtrando estadísticas');
    }
  }

  async openAffiliatePaymentModal(affiliatePaymentId: string, totalToPay: number, totalPaid: number): Promise<void> {
    const result = await this.modal.openAffiliatePaymentModal(affiliatePaymentId, totalToPay, totalPaid);
    if (!result?.paymentUpdated) return;
    const receivedaffiliatePaymentId = result.paymentUpdated.affiliatePaymentId;
    const indexToReplace = this.affiliateAnalytics.payments.findIndex((payment) => payment.affiliatePaymentId === receivedaffiliatePaymentId);
    if (indexToReplace === -1) return;
    this.affiliateAnalytics.payments[indexToReplace].totalPaid = result.paymentUpdated.totalPaid;
    this.affiliateAnalytics.payments[indexToReplace].paid = result.paymentUpdated.paid;
  }

  async openEditAffiliatePaymentModal(payment?: AffiliatePayment): Promise<void> {
    const result = await this.modal.openEditAffiliatePaymentModal(this.affiliateId, payment);
    if (!result?.paymentUpdated) return;
    const receivedaffiliatePaymentId = result.paymentUpdated.affiliatePaymentId;
    const indexToReplace = this.affiliateAnalytics.payments.findIndex((payment) => payment.affiliatePaymentId === receivedaffiliatePaymentId);
    if (indexToReplace === -1) {
      this.affiliateAnalytics.payments.unshift(result.paymentUpdated);
    } else {
      this.affiliateAnalytics.payments.splice(indexToReplace, 1, result.paymentUpdated);
    }
  }

  async confirmDeletePayment(i: number, affiliatePaymentId: string): Promise<any> {
    const header = 'Borrar pago';
    const message = `¿Estás seguro(a) de borrar este pago? Esta acción no se podrá deshacer y dejará de estar disponible.`;
    const noDelete = 'No borrar';
    const remove = 'Borrar';
    const deleteSection = await this.alert.presentConfirmActionAlert(header, message, noDelete, remove);
    return deleteSection ? this.deletePayment(i, affiliatePaymentId) : null;
  }

  async deletePayment(i: number, affiliatePaymentId: string): Promise<void> {
    try {
      this.affiliateAnalytics.payments[i].isDeleting = true;
      const body = { affiliatePaymentId };
      const response = await firstValueFrom(this.api.deleteAffiliatePayment(body));
      this.affiliateAnalytics.payments[i].isDeleting = false;
      this.affiliateAnalytics.payments.splice(i, 1);
      this.toast.presentSuccessToast(response.message);
    } catch (errorResponse: any) {
      this.affiliateAnalytics.payments[i].isDeleting = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error borrando afiliado');
    }
  }
}
